import { Controller } from '@hotwired/stimulus'
import { Modal } from 'bootstrap'

/**
 * EF定義フォームのStimulusコントローラー
 * 組み込み質問をYAMLテキストエリアに挿入する機能を提供する
 */
export default class extends Controller {
  static targets = ['yamlTextarea', 'modal', 'questionsContainer', 'loadingIndicator']
  static values = {
    questionsUrl: String,
  }

  connect () {
    this.questionsLoaded = false
    this.modal = null
  }

  /**
   * Bootstrapモーダルのインスタンスを取得（遅延初期化）
   */
  getModal () {
    if (!this.modal && this.hasModalTarget) {
      this.modal = new Modal(this.modalTarget)
    }
    return this.modal
  }

  /**
   * 組み込み質問モーダルを開く
   */
  async openModal () {
    if (!this.questionsLoaded) {
      await this.loadQuestions()
    }
    const modal = this.getModal()
    if (modal) {
      modal.show()
    }
  }

  /**
   * 組み込み質問一覧をAPIから取得してモーダルに表示
   */
  async loadQuestions () {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.style.display = ''
    }

    try {
      const response = await fetch(this.questionsUrlValue, {
        headers: {
          Accept: 'application/json',
        },
        credentials: 'same-origin',
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      this.renderQuestions(data.categories || [])
      this.questionsLoaded = true
    } catch (error) {
      console.error('[EF Definition Form] Failed to load builtin questions:', error)
      this.questionsContainerTarget.innerHTML = `
        <div class="alert alert-danger">
          組み込み質問の読み込みに失敗しました。ページを再読み込みしてください。
        </div>
      `
    } finally {
      if (this.hasLoadingIndicatorTarget) {
        this.loadingIndicatorTarget.style.display = 'none'
      }
    }
  }

  /**
   * カテゴリの色を取得
   */
  getCategoryColor (key) {
    const colors = {
      contact: { bg: '#e3f2fd', border: '#2196f3', text: '#1565c0' },
      resident: { bg: '#f3e5f5', border: '#9c27b0', text: '#7b1fa2' },
      area: { bg: '#e8f5e9', border: '#4caf50', text: '#2e7d32' },
    }
    return colors[key] || { bg: '#f5f5f5', border: '#9e9e9e', text: '#616161' }
  }

  /**
   * カテゴリ別の質問一覧をレンダリング（折りたたみ可能）
   */
  renderQuestions (categories) {
    const html = categories.map((category, index) => {
      const color = this.getCategoryColor(category.key)
      const collapseId = `category-collapse-${index}`
      return `
        <div class="mb-3">
          <div
            class="d-flex align-items-center justify-content-between p-2 rounded"
            style="background-color: ${color.bg}; border-left: 4px solid ${color.border}; cursor: pointer;"
            data-bs-toggle="collapse"
            data-bs-target="#${collapseId}"
            aria-expanded="true"
            aria-controls="${collapseId}"
          >
            <div class="d-flex align-items-center">
              <i class="fa fa-chevron-down me-2 collapse-icon" style="color: ${color.text}; transition: transform 0.2s;"></i>
              <span class="fw-bold" style="color: ${color.text};">${this.escapeHtml(category.name)}</span>
              <span class="badge ms-2 text-white" style="background-color: ${color.border};">${category.questions.length}</span>
            </div>
          </div>
          <div class="collapse show" id="${collapseId}">
            <div class="pt-2">
              ${category.questions.map(question => `
                <div
                  class="card mb-2 border-0 shadow-sm"
                  style="cursor: pointer;"
                  data-action="click->admin--inquiry--ef--definitions--form--component#insertQuestion"
                  data-yaml-snippet="${this.encodeYaml(question.yaml_snippet)}"
                  data-question-name="${this.escapeHtml(question.name)}"
                >
                  <div class="card-body py-2 px-3">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <span class="fw-semibold">${this.escapeHtml(question.name)}</span>
                        ${question.description ? `<br><small class="text-muted">${this.escapeHtml(question.description)}</small>` : ''}
                      </div>
                      <code class="text-muted small">${this.escapeHtml(question.key)}</code>
                    </div>
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
        </div>
      `
    }).join('')

    this.questionsContainerTarget.innerHTML = html

    // 折りたたみ時のアイコン回転を設定
    this.setupCollapseIcons()
  }

  /**
   * 折りたたみアイコンの回転アニメーション設定
   */
  setupCollapseIcons () {
    const collapses = this.questionsContainerTarget.querySelectorAll('.collapse')
    collapses.forEach(collapse => {
      const header = collapse.previousElementSibling
      const icon = header?.querySelector('.collapse-icon')
      if (!icon) return

      collapse.addEventListener('hide.bs.collapse', () => {
        icon.style.transform = 'rotate(-90deg)'
      })
      collapse.addEventListener('show.bs.collapse', () => {
        icon.style.transform = 'rotate(0deg)'
      })
    })
  }

  /**
   * 質問をYAMLテキストエリアに挿入
   */
  insertQuestion (event) {
    event.preventDefault()
    const button = event.currentTarget
    const encodedYaml = button.dataset.yamlSnippet
    const yamlSnippet = this.decodeYaml(encodedYaml)

    if (!yamlSnippet || !this.hasYamlTextareaTarget) {
      return
    }

    const textarea = this.yamlTextareaTarget
    const cursorPos = textarea.selectionStart
    const textBefore = textarea.value.substring(0, cursorPos)
    const textAfter = textarea.value.substring(cursorPos)

    // カーソル位置が行頭でない場合は改行を挿入
    const needsNewlineBefore = textBefore.length > 0 && !textBefore.endsWith('\n')
    const needsNewlineAfter = textAfter.length > 0 && !textAfter.startsWith('\n')

    const insertText =
      (needsNewlineBefore ? '\n' : '') +
      yamlSnippet +
      (needsNewlineAfter ? '\n' : '')

    textarea.value = textBefore + insertText + textAfter

    // カーソル位置を挿入したテキストの後ろに移動
    const newCursorPos = cursorPos + insertText.length
    textarea.setSelectionRange(newCursorPos, newCursorPos)
    textarea.focus()

    // モーダルを閉じる
    this.hideModal()
  }

  /**
   * モーダルを閉じる。
   * Bootstrap 4のModal#hide()は表示トランジション中（_isTransitioning）だと何もせずreturnする。
   * 質問一覧はフェードイン（約300ms）の最中に非同期ロードで表示されるため、直後のクリックでは
   * 「YAMLは挿入されるがモーダルが閉じない」レースになる（Issue #13658）。
   * トランジション完了を待ってからhideすることで確実に閉じる（最大50ms×40=2秒リトライ）
   */
  hideModal (attempts = 0) {
    const modal = this.getModal()
    if (!modal) {
      return
    }

    if (!modal._isTransitioning) {
      modal.hide()
      return
    }

    if (attempts < 40) {
      setTimeout(() => this.hideModal(attempts + 1), 50)
    }
  }

  /**
   * HTMLエスケープ
   */
  escapeHtml (text) {
    if (!text) return ''
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  /**
   * YAML文字列をBase64エンコード（改行を含むデータをdata属性に安全に格納）
   */
  encodeYaml (text) {
    if (!text) return ''
    return btoa(unescape(encodeURIComponent(text)))
  }

  /**
   * Base64エンコードされたYAML文字列をデコード
   */
  decodeYaml (encoded) {
    if (!encoded) return ''
    try {
      return decodeURIComponent(escape(atob(encoded)))
    } catch (e) {
      console.error('[EF Definition Form] Failed to decode yaml:', e)
      return ''
    }
  }
}
